import { useCallback, useRef } from "react";
import { createScanWizardTour } from "@/tours/scanWizardTour";
import { useAnalytics } from "@/hooks/useAnalytics";
import { waitForTourElements } from "@/utils/tourUtils";
import Shepherd from "shepherd.js";

const STORAGE_KEY = "scan_wizard_tour_completed";

/**
 * Get required tour elements for a given step and agent type.
 *
 * @param currentStep - Current wizard step (1, 2, 3, 4)
 * @param agentType - Selected agent type ("own", "our-ai", or null)
 * @returns Array of CSS selectors for required tour elements
 */
function getRequiredTourElements(currentStep: number, agentType: "own" | "our-ai" | null): string[] {
  switch (currentStep) {
    case 1:
      return ['[data-tour="connect-step"]'];
    case 2:
      if (agentType === "our-ai") {
        return ['[data-tour="provider-cards"]'];
      } else if (agentType === "own") {
        return ['[data-tour="own-agent-config"]'];
      }
      return [];
    case 3:
      return ['[data-tour="configure-plugins-step"]'];
    case 4:
      return ['[data-tour="configure-strategies-step"]'];
    default:
      return [];
  }
}

/**
 * useScanWizardTour Hook
 *
 * Manages the scan wizard tour lifecycle:
 * - Creates step-aware tours that adapt to the current wizard step
 * - Tracks tour events to PostHog
 * - Persists completion status to localStorage
 */
export function useScanWizardTour(
  currentStep: number,
  agentType: "own" | "our-ai" | null
) {
  const { track, posthog } = useAnalytics();
  const tourRef = useRef<InstanceType<typeof Shepherd.Tour> | null>(null);

  const hasCompletedTour = useCallback(() => {
    return localStorage.getItem(STORAGE_KEY) === "true";
  }, []);

  const markTourCompleted = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, "true");
    if (posthog) {
      posthog.people.set({ scan_wizard_tour_completed: true });
    }
  }, [posthog]);

  const startTour = useCallback(async () => {
    // Cancel any existing tour
    if (tourRef.current) {
      tourRef.current.cancel();
    }

    // Get required elements for current step
    const requiredElements = getRequiredTourElements(currentStep, agentType);

    if (requiredElements.length > 0) {
      try {
        await waitForTourElements(requiredElements, {
          maxWaitMs: 5000,
          initialDelayMs: 100,
          maxRetries: 10,
        });
      } catch (error) {
        console.warn("Some tour elements not ready, but starting tour anyway:", error);
      }
    }

    // Create tour for current step
    const tour = createScanWizardTour(currentStep, agentType);
    tourRef.current = tour;

    // Track tour start
    track({
      name: "tour_started" as any,
      properties: { tour_name: "scan_wizard", step: currentStep },
    });

    // Track step views
    tour.on("show", () => {
      const step = tour.getCurrentStep();
      if (step) {
        track({
          name: "tour_step_viewed" as any,
          properties: {
            tour_name: "scan_wizard",
            step: step.id,
            wizard_step: currentStep,
          },
        });
      }
    });

    // Track completion
    tour.on("complete", () => {
      markTourCompleted();
      track({
        name: "tour_completed" as any,
        properties: { tour_name: "scan_wizard", step: currentStep },
      });
    });

    // Track cancellation
    tour.on("cancel", () => {
      track({
        name: "tour_skipped" as any,
        properties: { tour_name: "scan_wizard", step: currentStep },
      });
    });

    tour.start();
  }, [currentStep, agentType, track, markTourCompleted]);

  const restartTour = useCallback(async () => {
    // Force restart by clearing completion status and starting tour
    localStorage.removeItem(STORAGE_KEY);
    if (posthog) {
      posthog.people.set({ scan_wizard_tour_completed: false });
    }

    // Cancel any existing tour first
    if (tourRef.current) {
      tourRef.current.cancel();
    }

    // Get required elements for current step
    const requiredElements = getRequiredTourElements(currentStep, agentType);

    // Wait for required tour elements to be available in the DOM
    if (requiredElements.length > 0) {
      try {
        await waitForTourElements(requiredElements, {
          maxWaitMs: 5000,
          initialDelayMs: 100,
          maxRetries: 10,
        });
      } catch (error) {
        // Log error but don't block tour - Shepherd's showOn() will handle missing elements
        console.warn("Some tour elements not ready, but starting tour anyway:", error);
      }
    }

    // Create and start new tour
    const tour = createScanWizardTour(currentStep, agentType);
    tourRef.current = tour;

    // Track tour start
    track({
      name: "tour_started" as any,
      properties: { tour_name: "scan_wizard", step: currentStep },
    });

    // Track step views
    tour.on("show", () => {
      const step = tour.getCurrentStep();
      if (step) {
        track({
          name: "tour_step_viewed" as any,
          properties: {
            tour_name: "scan_wizard",
            step: step.id,
            wizard_step: currentStep,
          },
        });
      }
    });

    // Track completion
    tour.on("complete", () => {
      markTourCompleted();
      track({
        name: "tour_completed" as any,
        properties: { tour_name: "scan_wizard", step: currentStep },
      });
    });

    // Track cancellation
    tour.on("cancel", () => {
      track({
        name: "tour_skipped" as any,
        properties: { tour_name: "scan_wizard", step: currentStep },
      });
    });

    tour.start();
  }, [currentStep, agentType, track, posthog, markTourCompleted]);

  return {
    hasCompletedTour,
    startTour,
    restartTour,
  };
}
